import { Link } from "react-router-dom";
import { FileText, AlertCircle, CheckCircle, Users, Scale, Phone, ChevronRight, Clock, Calendar } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from '../components/SEO';

const SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'Article',
  'headline': 'Mobbing am Arbeitsplatz: Was Sie jetzt tun können',
  'description': 'Mobbing am Arbeitsplatz erkennen, dokumentieren und melden. Schritt-für-Schritt-Anleitung mit rechtlichen Tipps für Betroffene in Deutschland.',
  'author': { '@type': 'Organization', 'name': 'mobbing-melden.de' },
  'publisher': { '@type': 'Organization', 'name': 'mobbing-melden.de', 'url': 'https://www.mobbing-melden.de' },
  'datePublished': '2025-05-08',
  'inLanguage': 'de-DE',
  'mainEntityOfPage': { '@type': 'WebPage', '@id': 'https://www.mobbing-melden.de/blog/mobbing-am-arbeitsplatz' },
};

const SIGNS = [
  "Kollegen oder Vorgesetzte reden regelmäßig schlecht über Sie — auch hinter Ihrem Rücken",
  "Sie werden von Besprechungen, Projekten oder Informationen systematisch ausgeschlossen",
  "Ihre Arbeitsergebnisse werden kleingemacht, ignoriert oder ohne Begründung kritisiert",
  "Sie erhalten absichtlich sinnlose, erniedrigende oder widersprüchliche Aufgaben",
  "Gerüchte über Sie werden absichtlich gestreut",
  "Sie werden vor anderen bloßgestellt, beleidigt oder lächerlich gemacht",
  "Ihr Arbeitsplatz, Ihre Werkzeuge oder Ihre Daten werden sabotiert",
];

const STEPS = [
  {
    number: 1,
    color: "#A0E8AF",
    icon: FileText,
    title: "Sofort: Dokumentieren",
    text: "Führen Sie ab sofort ein Mobbing-Protokoll. Notieren Sie jeden Vorfall mit Datum, Uhrzeit, Ort, Beteiligten und exakten Aussagen. Sichern Sie Screenshots, E-Mails und sonstige Beweise.",
    link: { label: "Protokoll-Vorlage kostenlos →", path: "/mobbing-protokoll" }
  },
  {
    number: 2,
    color: "#BDE0FE",
    icon: Users,
    title: "Betriebsrat einschalten",
    text: "Wenn ein Betriebsrat vorhanden ist: Beschweren Sie sich schriftlich. Der Betriebsrat hat nach § 84 BetrVG die Pflicht, Ihre Beschwerde zu prüfen und zu reagieren.",
    link: { label: "Betriebsrat-Ratgeber →", path: "/betriebsrat" }
  },
  {
    number: 3,
    color: "#FFC4D6",
    icon: AlertCircle,
    title: "Arbeitgeber schriftlich informieren",
    text: "Wenn der Mobber ein Kollege ist: Melden Sie es dem Vorgesetzten. Wenn der Vorgesetzte selbst mobbt: Melden Sie es der Personalabteilung oder der Geschäftsführung — immer schriftlich.",
    link: null
  },
  {
    number: 4,
    color: "#F6E05E",
    icon: Scale,
    title: "Rechtliche Schritte prüfen",
    text: "Wenn innerbetriebliche Wege scheitern: Anwalt für Arbeitsrecht konsultieren. Das Arbeitsgericht ist zuständig und im ersten Rechtszug entstehen keine Anwaltskosten.",
    link: { label: "Rechtliche Schritte →", path: "/anwalt" }
  },
  {
    number: 5,
    color: "#A0E8AF",
    icon: Phone,
    title: "Gesundheit schützen",
    text: "Mobbing macht krank. Sprechen Sie mit Ihrem Hausarzt — bei einer Krankschreibung durch Mobbing-Folgen besteht Anspruch auf Lohnfortzahlung. Suchen Sie psychologische Unterstützung.",
    link: null
  },
];

const DONT_DOS = [
  { wrong: "Konfrontation ohne Zeugen", better: "Gespräche immer mit einer dritten Person führen oder schriftlich" },
  { wrong: "Nur mündlich beschweren", better: "Alles schriftlich — auch interne Mails dokumentieren" },
  { wrong: "Zu lange warten", better: "Schon beim ersten wiederholten Vorfall handeln" },
  { wrong: "Beweise nicht sichern", better: "Screenshots, E-Mails und Notizen sofort sichern" },
  { wrong: "Kündigung voreilig einreichen", better: "Zuerst rechtliche Beratung — Abfindung oder Aufhebungsvertrag prüfen" },
];

const LEGAL_RIGHTS = [
  { para: "§ 84 BetrVG", text: "Beschwerderecht beim Betriebsrat" },
  { para: "§ 75 BetrVG", text: "Arbeitgeber muss Persönlichkeitsrechte schützen" },
  { para: "§ 618 BGB", text: "Fürsorgepflicht des Arbeitgebers" },
  { para: "§ 823 BGB", text: "Schadensersatz bei Persönlichkeitsrechtsverletzung" },
  { para: "§ 253 BGB", text: "Schmerzensgeld bei Gesundheitsschäden" },
  { para: "AGG", text: "Schutz vor diskriminierendem Mobbing" },
];

export default function BlogArtikelArbeitsplatz() {
  return (
    <div className="min-h-screen bg-[#FFFDF9]">
      <Header />
      <SEO
        title="Mobbing am Arbeitsplatz: Was Sie jetzt tun können"
        description="Mobbing am Arbeitsplatz erkennen, dokumentieren und melden. Schritt-für-Schritt-Anleitung mit rechtlichen Tipps, Betriebsrat und Anwalt für Betroffene in Deutschland."
        path="/blog/mobbing-am-arbeitsplatz"
        schema={SCHEMA}
      />

      <main className="px-6 md:px-12 lg:px-24 py-12">
        <div className="max-w-3xl mx-auto">

          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm text-[#A0AEC0] mb-8">
            <Link to="/" className="hover:text-[#2D3748]">Start</Link>
            <span>/</span>
            <Link to="/blog" className="hover:text-[#2D3748]">Ratgeber</Link>
            <span>/</span>
            <span className="text-[#2D3748]">Mobbing am Arbeitsplatz</span>
          </nav>

          {/* Article header */}
          <header className="mb-10">
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              <span className="bg-[#A0E8AF]/30 text-[#1A4731] text-xs font-bold px-3 py-1 rounded-full">Arbeitsplatz</span>
              <span className="flex items-center gap-1 text-[#A0AEC0] text-xs"><Clock className="w-3 h-3" /> 7 Min Lesezeit</span>
              <span className="flex items-center gap-1 text-[#A0AEC0] text-xs"><Calendar className="w-3 h-3" /> Mai 2025</span>
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-[#2D3748] mb-4 leading-tight" style={{ fontFamily: 'Nunito' }}>
              Mobbing am Arbeitsplatz: Was Sie jetzt tun können
            </h1>
            <p className="text-lg text-[#718096] leading-relaxed">
              Mobbing am Arbeitsplatz ist weit verbreitet — rund 1,5 Millionen Arbeitnehmer in Deutschland sind jährlich betroffen. Dieser Ratgeber zeigt, wie Sie es erkennen, was Sie rechtlich tun können und warum schnelles Handeln entscheidend ist.
            </p>
          </header>

          {/* CTA box */}
          <div className="bg-[#A0E8AF]/15 border border-[#A0E8AF] rounded-2xl p-5 mb-10 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-sm text-[#2D3748] font-medium">Direkt betroffen? Melden Sie den Fall jetzt anonym und kostenlos.</p>
            <Link to="/melden" className="inline-flex items-center gap-2 bg-[#A0E8AF] text-[#1A4731] font-bold px-5 py-2.5 rounded-full hover:bg-[#86D696] transition-all text-sm whitespace-nowrap">
              Anonym melden <ChevronRight className="w-4 h-4" />
            </Link>
          </div>

          {/* Section 1: Was ist Mobbing */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-[#2D3748] mb-4" style={{ fontFamily: 'Nunito' }}>Was ist Mobbing am Arbeitsplatz?</h2>
            <p className="text-[#718096] mb-4 leading-relaxed">
              Von Mobbing spricht man, wenn ein Arbeitnehmer über einen längeren Zeitraum — in der Regel mindestens 6 Monate — systematisch und wiederholt schikaniert, benachteiligt oder ausgegrenzt wird. Das Schlüsselwort ist <strong className="text-[#2D3748]">systematisch</strong>: ein einzelner schlechter Tag oder ein einmaliger Konflikt ist kein Mobbing.
            </p>
            <p className="text-[#718096] mb-4 leading-relaxed">
              Mobbing am Arbeitsplatz kann von Kollegen ausgehen (<em>horizontales Mobbing</em>) oder von Vorgesetzten (<em>Bossing</em>). Beide Formen sind gleich ernst zu nehmen — der Arbeitgeber hat in beiden Fällen eine Schutzpflicht.
            </p>
          </section>

          {/* Section 2: Anzeichen */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-[#2D3748] mb-4" style={{ fontFamily: 'Nunito' }}>Typische Anzeichen für Mobbing</h2>
            <p className="text-[#718096] mb-4 leading-relaxed">
              Mobbing ist oft schleichend — viele Betroffene zweifeln lange an sich selbst. Diese Verhaltensweisen können auf Mobbing hindeuten, wenn sie sich wiederholen:
            </p>
            <ul className="space-y-2">
              {SIGNS.map((sign, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-[#4A5568]">
                  <AlertCircle className="w-4 h-4 text-[#FFC4D6] flex-shrink-0 mt-0.5" />
                  {sign}
                </li>
              ))}
            </ul>
            <div className="bg-gray-50 rounded-xl p-4 mt-4 text-sm text-[#718096]">
              <strong className="text-[#2D3748]">Wichtig:</strong> Nicht jeder Stress am Arbeitsplatz ist Mobbing. Entscheidend sind <em>Absicht, Wiederholung und Systematik</em>. Im Zweifelsfall: Protokoll führen und rechtliche Beratung einholen.
            </div>
          </section>

          {/* Section 3: Steps */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-[#2D3748] mb-6" style={{ fontFamily: 'Nunito' }}>Was tun? — Die 5 wichtigsten Schritte</h2>
            <div className="space-y-4">
              {STEPS.map((step, i) => {
                const Icon = step.icon;
                return (
                  <div key={i} className="bg-white rounded-2xl border border-gray-100 p-5 flex gap-4">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 font-bold text-white text-sm" style={{ backgroundColor: step.color }}>
                      {step.number}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Icon className="w-4 h-4 text-[#A0AEC0]" />
                        <h3 className="font-bold text-[#2D3748]">{step.title}</h3>
                      </div>
                      <p className="text-[#718096] text-sm mb-2">{step.text}</p>
                      {step.link && (
                        <Link to={step.link.path} className="inline-flex items-center gap-1 text-[#1A4731] text-xs font-bold hover:underline">
                          {step.link.label}
                        </Link>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          {/* Section 4: Fehler vermeiden */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-[#2D3748] mb-4" style={{ fontFamily: 'Nunito' }}>Diese Fehler machen Betroffene häufig</h2>
            <div className="space-y-3">
              {DONT_DOS.map((item, i) => (
                <div key={i} className="bg-white rounded-xl border border-gray-100 p-4 grid grid-cols-2 gap-4 text-sm">
                  <div className="flex items-start gap-2">
                    <span className="text-[#C53030] font-bold flex-shrink-0">✗</span>
                    <span className="text-[#718096]">{item.wrong}</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="text-[#1A4731] font-bold flex-shrink-0">✓</span>
                    <span className="text-[#4A5568]">{item.better}</span>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Section 5: Rechtslage */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-[#2D3748] mb-4" style={{ fontFamily: 'Nunito' }}>Rechtslage in Deutschland</h2>
            <p className="text-[#718096] mb-4 leading-relaxed">
              Es gibt kein eigenes "Anti-Mobbing-Gesetz" in Deutschland. Betroffene können sich jedoch auf verschiedene Gesetze stützen:
            </p>
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
              {LEGAL_RIGHTS.map((r, i) => (
                <div key={i} className={`flex gap-4 px-5 py-3 text-sm ${i % 2 === 0 ? '' : 'bg-gray-50/50'}`}>
                  <span className="font-mono font-bold text-[#1A365D] w-28 flex-shrink-0">{r.para}</span>
                  <span className="text-[#718096]">{r.text}</span>
                </div>
              ))}
            </div>
            <p className="text-[#718096] text-sm mt-4 leading-relaxed">
              Das Arbeitsgericht ist für Mobbing-Fälle zuständig. Im ersten Rechtszug tragen beide Seiten ihre eigenen Anwaltskosten — das Kostenrisiko ist überschaubar. Gewerkschaftsmitglieder haben oft kostenlosen Rechtsschutz.
            </p>
          </section>

          {/* Section 6: Gesundheit */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-[#2D3748] mb-4" style={{ fontFamily: 'Nunito' }}>Gesundheit geht vor</h2>
            <p className="text-[#718096] mb-4 leading-relaxed">
              Langfristiges Mobbing kann ernsthafte Folgen haben: Burnout, Depressionen, Angststörungen, Schlafprobleme und körperliche Beschwerden. Wenn Sie merken, dass Ihre Gesundheit leidet:
            </p>
            <ul className="space-y-2 text-sm text-[#4A5568]">
              {[
                "Sprechen Sie mit Ihrem Hausarzt — auch über psychische Belastungen",
                "Lassen Sie sich krankschreiben, wenn nötig. Mobbing-bedingte Erkrankungen sind legitim.",
                "Suchen Sie psychologische Unterstützung (Wartezeiten bei Psychologen nutzen: Kostenerstattungsverfahren bei Kasse anfragen)",
                "Vertrauen Sie sich einem Menschen an — Isolation verstärkt die Belastung",
              ].map((tip, i) => (
                <li key={i} className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-[#1A4731] flex-shrink-0 mt-0.5" />
                  {tip}
                </li>
              ))}
            </ul>
          </section>

          {/* Fazit */}
          <section className="mb-10 bg-white rounded-2xl border border-gray-100 p-6">
            <h2 className="text-xl font-bold text-[#2D3748] mb-3" style={{ fontFamily: 'Nunito' }}>Fazit</h2>
            <p className="text-[#718096] leading-relaxed">
              Mobbing am Arbeitsplatz ist ernst — und Sie müssen es nicht hinnehmen. Die wichtigste Sofortmaßnahme ist <strong className="text-[#2D3748]">Dokumentation</strong>: Ohne Beweise ist es schwer, etwas zu beweisen. Beginnen Sie noch heute mit einem Protokoll, schalten Sie den Betriebsrat ein und holen Sie sich rechtliche Beratung. Je früher Sie handeln, desto stärker Ihre Position.
            </p>
          </section>

          {/* Related articles */}
          <section className="mb-10">
            <h2 className="text-xl font-bold text-[#2D3748] mb-4" style={{ fontFamily: 'Nunito' }}>Weiterführende Ratgeber</h2>
            <div className="grid md:grid-cols-3 gap-3">
              {[
                { emoji: "📋", label: "Mobbing Protokoll", path: "/mobbing-protokoll" },
                { emoji: "👥", label: "Betriebsrat einschalten", path: "/betriebsrat" },
                { emoji: "⚖️", label: "Rechtliche Schritte", path: "/anwalt" },
              ].map((item, i) => (
                <Link key={i} to={item.path} className="bg-white rounded-xl border border-gray-100 p-4 flex items-center gap-3 hover:border-gray-300 transition-colors text-sm font-medium text-[#2D3748]">
                  <span className="text-xl">{item.emoji}</span>
                  {item.label}
                  <ChevronRight className="w-4 h-4 text-[#A0AEC0] ml-auto" />
                </Link>
              ))}
            </div>
          </section>

          {/* CTA */}
          <div className="bg-gradient-to-br from-[#E6FFFA] to-[#EBF8FF] rounded-2xl p-8 text-center">
            <h2 className="text-2xl font-bold text-[#2D3748] mb-3" style={{ fontFamily: 'Nunito' }}>
              Jetzt anonym Mobbing melden
            </h2>
            <p className="text-[#718096] mb-6 max-w-xl mx-auto">
              Kein Name, keine Registrierung. Beschreiben Sie den Fall, laden Sie Beweise hoch — wir helfen Ihnen beim nächsten Schritt.
            </p>
            <Link to="/melden" className="inline-flex items-center justify-center gap-2 bg-[#A0E8AF] text-[#1A4731] font-bold px-8 py-4 rounded-full hover:bg-[#86D696] transition-all">
              <FileText className="w-5 h-5" />
              Kostenlos melden
            </Link>
          </div>

        </div>
      </main>
      <Footer />
    </div>
  );
}
